import React, { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Activity, CheckSquare, ChevronLeft, ChevronRight, Filter, RefreshCw, X } from 'lucide-react';
import { DashboardData } from '../../types';
import { SyncDetailsModal } from '../../components/modals/SyncDetailsModal';
import { SyncActivityCard } from '../../components/sync/SyncActivityCard';
import { adminAPI } from '../../services/api/adminAPI';
import { usePermissions } from '../../hooks/usePermissions';
import { CustomSelect } from '../../components/common/CustomSelect';
import { SearchWithSuggestions } from '../../components/common/SearchWithSuggestions';
import { useToast } from '../../hooks/useToast';
import { ToastContainer } from '../../components/common/ToastContainer';
import { ConfirmDialog } from '../../components/common/ConfirmDialog';

interface OutletContext {
	dashboardData: DashboardData | null;
	refreshKey: number;
}

export const IntegrationHealthPage: React.FC = () => {
	const { dashboardData, refreshKey } = useOutletContext<OutletContext>();
	const { isClientUser, clientId, clientName } = usePermissions();
	const { toasts, removeToast, success, error } = useToast();
	const [showDetailsModal, setShowDetailsModal] = useState(false);
	const [selectedActivity, setSelectedActivity] = useState(null);
	const [currentPage, setCurrentPage] = useState(1);
	const [syncActivities, setSyncActivities] = useState<any[]>([]);
	const [pagination, setPagination] = useState({
		currentPage: 1,
		totalPages: 1,
		totalItems: 0,
		itemsPerPage: 100,
	});
	const [activitiesLoading, setActivitiesLoading] = useState(false);
	const [retryingIds, setRetryingIds] = useState<Set<string>>(new Set());
	const [markingManuallyIds, setMarkingManuallyIds] = useState<Set<string>>(new Set());
	const [showFilters, setShowFilters] = useState(false);
	const [showConfirmDialog, setShowConfirmDialog] = useState(false);
	const [pendingSyncLogId, setPendingSyncLogId] = useState<string | null>(null);
	const [selectedSyncLogs, setSelectedSyncLogs] = useState<Set<string>>(new Set());
	const [isBulkRetrying, setIsBulkRetrying] = useState(false);
	const [showBulkConfirmDialog, setShowBulkConfirmDialog] = useState(false);
	const [bulkRetryMode, setBulkRetryMode] = useState<'selected' | 'currentPage' | 'allPages'>('selected');
	const [totalFailedCount, setTotalFailedCount] = useState<number>(0);
	const [filters, setFilters] = useState({
		startDate: '',
		endDate: '',
		entityType: '',
		status: '',
		entityId: '',
		searchType: 'entityId', // 'entityId' or 'error'
		searchText: '',
	});
	const itemsPerPage = 100;

	const handleStatusClick = (activity: any) => {
		setSelectedActivity(activity);
		setShowDetailsModal(true);
	};

	const handleCloseDetailsModal = () => {
		setShowDetailsModal(false);
		setSelectedActivity(null);
	};

	const fetchSyncActivities = async (page: number) => {
		setActivitiesLoading(true);
		try {
			// For CLIENT_USER: explicitly pass clientId (even though backend enforces it)
			// For ADMIN: pass undefined to see all logs
			const filterClientId = isClientUser ? clientId : undefined;

			// Build API filters based on search type
			const apiFilters: any = {
				startDate: filters.startDate,
				endDate: filters.endDate,
				entityType: filters.entityType,
				status: filters.status,
			};

			// Add search parameter based on selected search type
			if (filters.searchText) {
				if (filters.searchType === 'entityId') {
					apiFilters.entityId = filters.searchText;
				} else if (filters.searchType === 'error') {
					apiFilters.errorText = filters.searchText;
				}
			}

			const response = await adminAPI.getSyncActivities(page, itemsPerPage, filterClientId, apiFilters);
			setSyncActivities(response.activities);
			setPagination(response.pagination);
			setCurrentPage(page);

			// Fetch total failed count across all pages
			await fetchTotalFailedCount(filterClientId, apiFilters);
		} catch (error) {
			console.error('Failed to fetch sync activities:', error);

			// Fallback to dashboard data if API fails
			if (dashboardData?.recentActivity) {
				setSyncActivities(dashboardData.recentActivity);
				setPagination({
					currentPage: 1,
					totalPages: Math.ceil(dashboardData.recentActivity.length / itemsPerPage),
					totalItems: dashboardData.recentActivity.length,
					itemsPerPage,
				});
			}
		} finally {
			setActivitiesLoading(false);
		}
	};

	const fetchTotalFailedCount = async (filterClientId: string | undefined, apiFilters: any) => {
		try {
			// Fetch with FAILED status to get total count
			const failedFilters = { ...apiFilters, status: 'FAILED' };
			const response = await adminAPI.getSyncActivities(
				1,
				1, // Just fetch 1 item to get the count
				filterClientId,
				failedFilters,
			);
			setTotalFailedCount(response.pagination.totalItems);
		} catch (error) {
			console.error('Failed to fetch total failed count:', error);
			setTotalFailedCount(0);
		}
	};

	useEffect(() => {
		if (refreshKey > 0) {
			fetchSyncActivities(1);
		}
	}, [refreshKey]);

	useEffect(() => {
		fetchSyncActivities(1);
	}, [isClientUser, clientId, filters]); // Re-fetch when user context or filters change

	const handleFilterChange = (key: string, value: string) => {
		setFilters((prev) => ({ ...prev, [key]: value }));
		setCurrentPage(1); // Reset to page 1 when filters change
	};

	const handleClearFilters = () => {
		setFilters({
			startDate: '',
			endDate: '',
			entityType: '',
			status: '',
			entityId: '',
			searchType: 'entityId',
			searchText: '',
		});
		setCurrentPage(1);
	};

	const hasActiveFilters = Object.entries(filters).some(([key, value]) => key !== 'searchType' && value !== '');

	const handlePreviousPage = () => {
		const newPage = Math.max(currentPage - 1, 1);
		fetchSyncActivities(newPage);
	};

	const handleNextPage = () => {
		const newPage = Math.min(currentPage + 1, pagination.totalPages);
		fetchSyncActivities(newPage);
	};

	const handleRetry = async (syncLogId: string, event: React.MouseEvent) => {
		event.stopPropagation();

		setRetryingIds((prev) => new Set(prev).add(syncLogId));

		try {
			// Start the retry API call
			const retryPromise = adminAPI.retrySyncLog(syncLogId);

			// Ensure minimum 2 second display time for the retrying state
			const minDelayPromise = new Promise((resolve) => setTimeout(resolve, 2000));

			// Wait for both the API call and the minimum delay
			await Promise.all([retryPromise, minDelayPromise]);

			// Refresh the activities list after successful retry
			await fetchSyncActivities(currentPage);
			success('Sync retry initiated successfully');
		} catch (err) {
			console.error('Failed to retry sync:', err);
			error('Failed to retry sync. Please try again.');
		} finally {
			setRetryingIds((prev) => {
				const newSet = new Set(prev);
				newSet.delete(syncLogId);
				return newSet;
			});
		}
	};

	const handleMarkAsManuallyTriggered = async (syncLogId: string, event: React.MouseEvent) => {
		event.stopPropagation();

		// Show confirmation dialog
		setPendingSyncLogId(syncLogId);
		setShowConfirmDialog(true);
	};

	const confirmMarkAsManuallyTriggered = async () => {
		if (!pendingSyncLogId) return;

		setMarkingManuallyIds((prev) => new Set(prev).add(pendingSyncLogId));

		try {
			// Call API to mark as manually triggered
			await adminAPI.markAsManuallyTriggered(pendingSyncLogId);

			// Refresh the activities list after successful marking
			await fetchSyncActivities(currentPage);
			success('Sync marked as manually triggered successfully');
		} catch (err) {
			console.error('Failed to mark as manually triggered:', err);
			error('Failed to mark sync as manually triggered. Please try again.');
		} finally {
			setMarkingManuallyIds((prev) => {
				const newSet = new Set(prev);
				newSet.delete(pendingSyncLogId);
				return newSet;
			});
			setPendingSyncLogId(null);
		}
	};

	const handleSelectSyncLog = (syncLogId: string, isSelected: boolean) => {
		setSelectedSyncLogs((prev) => {
			const newSet = new Set(prev);
			if (isSelected) {
				newSet.add(syncLogId);
			} else {
				newSet.delete(syncLogId);
			}
			return newSet;
		});
	};

	const handleSelectAll = () => {
		const failedSyncs = syncActivities.filter((activity) => activity.status === 'FAILED');
		setSelectedSyncLogs(new Set(failedSyncs.map((activity) => activity.id)));
	};

	const handleDeselectAll = () => {
		setSelectedSyncLogs(new Set());
	};

	const handleBulkRetrySelected = () => {
		setBulkRetryMode('selected');
		setShowBulkConfirmDialog(true);
	};

	const handleBulkRetryCurrentPage = () => {
		setBulkRetryMode('currentPage');
		setShowBulkConfirmDialog(true);
	};

	const handleBulkRetryAllPages = () => {
		setBulkRetryMode('allPages');
		setShowBulkConfirmDialog(true);
	};

	const confirmBulkRetry = async () => {
		setIsBulkRetrying(true);
		try {
			let payload: any;

			if (bulkRetryMode === 'selected') {
				// Retry selected sync logs
				payload = {
					syncLogIds: Array.from(selectedSyncLogs),
				};
			} else if (bulkRetryMode === 'currentPage') {
				// Retry all failed syncs on CURRENT PAGE only
				const failedSyncIds = syncActivities
					.filter((activity) => activity.status === 'FAILED')
					.map((activity) => activity.id);

				payload = {
					syncLogIds: failedSyncIds,
				};
			} else {
				// Retry all failed syncs across ALL PAGES matching current filters
				const apiFilters: any = {
					status: 'FAILED',
					startDate: filters.startDate,
					endDate: filters.endDate,
					entityType: filters.entityType,
				};

				if (filters.searchText) {
					if (filters.searchType === 'entityId') {
						apiFilters.entityId = filters.searchText;
					} else if (filters.searchType === 'error') {
						apiFilters.errorText = filters.searchText;
					}
				}

				// Add clientId for CLIENT_USER
				if (isClientUser) {
					apiFilters.clientId = clientId;
				}

				payload = { filters: apiFilters };
			}

			const result = await adminAPI.bulkRetrySyncLogs(payload);

			// Clear selections
			setSelectedSyncLogs(new Set());

			// Refresh the activities list
			await fetchSyncActivities(currentPage);

			// Show success message — retry is fire-and-forget, backend only returns total queued
			if (result.total > 0) {
				success(`✓ ${result.total} sync${result.total === 1 ? '' : 's'} queued for retry`);
			} else {
				error(`Failed to queue any syncs for retry. Please check the logs.`);
			}
		} catch (err) {
			console.error('Failed to bulk retry syncs:', err);
			error('Failed to bulk retry syncs. Please try again.');
		} finally {
			setIsBulkRetrying(false);
			setShowBulkConfirmDialog(false);
		}
	};

	if (!dashboardData || (isClientUser && !clientName)) {
		return (
			<div className="flex items-center justify-center min-h-[400px]">
				<div className="text-center">
					<RefreshCw className="animate-spin h-8 w-8 text-blue-600 mx-auto mb-2" />
					<p className="text-sm text-gray-600">Loading dashboard...</p>
				</div>
			</div>
		);
	}

	// Calculate display indices for the current page
	const startIndex = (pagination.currentPage - 1) * pagination.itemsPerPage;
	const endIndex = Math.min(startIndex + pagination.itemsPerPage, pagination.totalItems);

	return (
		<div className="space-y-6">
			{/* CLIENT CONTEXT BANNER - Show for CLIENT_USERs */}
			{isClientUser && clientName && (
				<div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6">
					<div className="flex items-center space-x-3">
						<div className="flex-shrink-0">
							<div className="h-12 w-12 bg-blue-600 rounded-lg flex items-center justify-center">
								<Activity className="h-6 w-6 text-white" />
							</div>
						</div>
						<div className="flex-1">
							<h3 className="text-lg font-semibold text-gray-900">{clientName} - Sync Activity</h3>
							<p className="text-sm text-gray-600">Viewing sync logs and operations for your integration</p>
						</div>
					</div>
				</div>
			)}

			<div className="bg-white rounded-xl border border-gray-200">
				<div className="px-6 py-4 border-b border-gray-200">
					<div className="flex items-center justify-between mb-4">
						<div>
							<h3 className="text-lg font-semibold text-gray-900">Recent Sync Activity</h3>
							<p className="text-sm text-gray-500 mt-1">
								{syncActivities.filter((a) => a.status === 'FAILED').length > 0 ? (
									<>
										<span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
											{syncActivities.filter((a) => a.status === 'FAILED').length} failed
										</span>{' '}
										<span className="text-gray-400">•</span> Select individual syncs or use bulk retry options below
									</>
								) : (
									'Click on status badges to view detailed information'
								)}
							</p>
						</div>
						<div className="flex items-center space-x-2">
							{pagination.totalItems > 0 && (
								<div className="text-sm text-gray-500 mr-2">
									Showing {startIndex + 1}-{endIndex} of {pagination.totalItems}
								</div>
							)}
							<button
								onClick={() => setShowFilters(!showFilters)}
								className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${
									showFilters || hasActiveFilters
										? 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100'
										: 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
								}`}>
								<Filter className="h-4 w-4 mr-2" />
								Filters
								{hasActiveFilters && (
									<span className="ml-2 inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-blue-600 rounded-full">
										{Object.entries(filters).filter(([key, value]) => key !== 'searchType' && value !== '').length}
									</span>
								)}
							</button>
						</div>
					</div>

					{/* Bulk Actions Panel - Show when there are failed syncs */}
					{syncActivities.filter((a) => a.status === 'FAILED').length > 0 && (
						<div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 mb-4 border border-blue-200">
							<div className="flex items-center justify-between">
								<div className="flex items-center space-x-3">
									<div className="flex-shrink-0">
										<div className="h-10 w-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center shadow-sm">
											<RefreshCw className="h-5 w-5 text-white" />
										</div>
									</div>
									<div>
										<h4 className="text-sm font-semibold text-gray-900">Bulk Retry Options</h4>
										<p className="text-xs text-gray-600 mt-0.5">
											{selectedSyncLogs.size > 0 ? (
												<>
													<span className="font-semibold text-blue-700">
														{selectedSyncLogs.size} sync
														{selectedSyncLogs.size === 1 ? '' : 's'} selected
													</span>{' '}
													• Ready to retry
												</>
											) : (
												<>
													<span className="font-medium text-gray-700">
														{syncActivities.filter((a) => a.status === 'FAILED').length} failed on current page
													</span>
													{' • '}
													<span className="font-medium text-gray-700">
														{totalFailedCount} total failed across all pages
													</span>
												</>
											)}
										</p>
									</div>
								</div>
								<div className="flex items-center space-x-2">
									{selectedSyncLogs.size > 0 ? (
										<>
											<button
												onClick={handleDeselectAll}
												className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 shadow-sm transition-all duration-200">
												<X className="h-4 w-4 mr-2" />
												Clear ({selectedSyncLogs.size})
											</button>
											<button
												onClick={handleBulkRetrySelected}
												disabled={isBulkRetrying}
												className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg hover:from-blue-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg transition-all duration-200">
												{isBulkRetrying ? (
													<>
														<RefreshCw className="animate-spin h-4 w-4 mr-2" />
														Retrying...
													</>
												) : (
													<>
														<RefreshCw className="h-4 w-4 mr-2" />
														Retry Selected ({selectedSyncLogs.size})
													</>
												)}
											</button>
										</>
									) : (
										<>
											<button
												onClick={handleSelectAll}
												className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 shadow-sm transition-all duration-200">
												<CheckSquare className="h-4 w-4 mr-2" />
												Select All Failed
											</button>
											<button
												onClick={handleBulkRetryCurrentPage}
												disabled={isBulkRetrying}
												className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg hover:from-blue-700 hover:to-blue-800 disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg transition-all duration-200">
												{isBulkRetrying ? (
													<>
														<RefreshCw className="animate-spin h-4 w-4 mr-2" />
														Retrying...
													</>
												) : (
													<>
														<RefreshCw className="h-4 w-4 mr-2" />
														Retry Current Page ({syncActivities.filter((a) => a.status === 'FAILED').length})
													</>
												)}
											</button>
											<button
												onClick={handleBulkRetryAllPages}
												disabled={isBulkRetrying || totalFailedCount === 0}
												className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-orange-600 to-red-600 rounded-lg hover:from-orange-700 hover:to-red-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg transition-all duration-200">
												{isBulkRetrying ? (
													<>
														<RefreshCw className="animate-spin h-4 w-4 mr-2" />
														Retrying...
													</>
												) : (
													<>
														<RefreshCw className="h-4 w-4 mr-2" />
														Retry All Failed ({totalFailedCount})
													</>
												)}
											</button>
										</>
									)}
								</div>
							</div>
						</div>
					)}

					{/* Filter Panel */}
					{showFilters && (
						<div className="bg-gray-50 rounded-lg p-4 pb-8 mb-6 border border-gray-200">
							<div className="grid grid-cols-5 gap-4">
								{/* Start Date */}
								<div>
									<label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
									<input
										type="date"
										value={filters.startDate}
										onChange={(e) => handleFilterChange('startDate', e.target.value)}
										className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-gray-400 transition-colors"
									/>
								</div>

								{/* End Date */}
								<div>
									<label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
									<input
										type="date"
										value={filters.endDate}
										onChange={(e) => handleFilterChange('endDate', e.target.value)}
										className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-gray-400 transition-colors"
									/>
								</div>

								{/* Entity Type */}
								<div>
									<label className="block text-sm font-medium text-gray-700 mb-1">Entity Type</label>
									<CustomSelect
										value={filters.entityType}
										onChange={(value) => handleFilterChange('entityType', value)}
										options={[
											{ value: '', label: 'All Types' },
											{ value: 'CUSTOMER', label: 'Customer' },
											{ value: 'VENDOR', label: 'Vendor' },
											{ value: 'PART', label: 'Part' },
											{ value: 'SERVICE', label: 'Service' },
											{ value: 'INVOICE', label: 'Invoice' },
											{ value: 'PAYMENT', label: 'Payment' },
											{ value: 'CREDIT_NOTE', label: 'Credit Note' },
											{ value: 'EXPENSE', label: 'Expense' },
											{ value: 'IBT_SENT', label: 'IBT Sent' },
											{ value: 'IBT_RECEIVED', label: 'IBT Received' },
											{ value: 'STOCK_IN', label: 'Stock In' },
											{ value: 'STOCK_ADJUSTMENT', label: 'Stock Adjustment' },
											{
												value: 'BATCH_TO_BATCH_TRANSFER',
												label: 'Batch to Batch Transfer',
											},
											{ value: 'PURCHASE_RETURN', label: 'Purchase Return' },
											{ value: 'PURCHASE_ORDER', label: 'Purchase Order' },
											{
												value: 'CUSTOMER_PART_PRICE',
												label: 'Customer Part Price',
											},
											{
												value: 'CUSTOMER_SERVICE_PRICE',
												label: 'Customer Service Price',
											},
											{
												value: 'CUSTOMER_GROUP_PART_PRICE',
												label: 'Customer Group Part Price',
											},
											{
												value: 'CUSTOMER_GROUP_SERVICE_PRICE',
												label: 'Customer Group Service Price',
											},
										]}
									/>
								</div>

								{/* Status */}
								<div>
									<label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
									<CustomSelect
										value={filters.status}
										onChange={(value) => handleFilterChange('status', value)}
										options={[
											{ value: '', label: 'All Status' },
											{ value: 'COMPLETED', label: 'Completed' },
											{ value: 'FAILED', label: 'Failed' },
											{ value: 'PENDING', label: 'Pending' },
											{
												value: 'MANUALLY_TRIGGERED',
												label: 'Manually Triggered',
											},
										]}
									/>
								</div>

								{/* Search with Suggestions */}
								<div>
									<label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
									<SearchWithSuggestions
										searchText={filters.searchText}
										onSearchChange={(text, category) => {
											handleFilterChange('searchText', text);
											handleFilterChange('searchType', category);
										}}
										placeholder="Search by Entity ID or Error Message..."
									/>
								</div>
							</div>

							{/* Filter Actions */}
							{hasActiveFilters && (
								<div className="mt-4 flex items-center justify-end">
									<button
										onClick={handleClearFilters}
										className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
										<X className="h-4 w-4 mr-1" />
										Clear Filters
									</button>
								</div>
							)}
						</div>
					)}
				</div>
				<div className="p-6">
					{activitiesLoading ? (
						<div className="text-center py-8">
							<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
							<p className="mt-2 text-sm text-gray-500">Loading activities...</p>
						</div>
					) : pagination.totalItems === 0 ? (
						<div className="text-center py-8">
							<Activity className="mx-auto h-12 w-12 text-gray-400" />
							<h3 className="mt-2 text-sm font-medium text-gray-900">No recent activity</h3>
							<p className="mt-1 text-sm text-gray-500">Sync activities will appear here when they occur.</p>
						</div>
					) : (
						<>
							<div className="space-y-4">
								{syncActivities.map((activity) => (
									<div key={activity.id}>
										<SyncActivityCard
											activity={activity}
											onRetry={handleRetry}
											isRetrying={retryingIds.has(activity.id)}
											onMarkAsManuallyTriggered={handleMarkAsManuallyTriggered}
											isMarkingManually={markingManuallyIds.has(activity.id)}
											onClick={() => handleStatusClick(activity)}
											isSelected={selectedSyncLogs.has(activity.id)}
											onSelect={handleSelectSyncLog}
										/>
									</div>
								))}
							</div>

							{/* Pagination Controls */}
							{pagination.totalPages > 1 && (
								<div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
									<div className="flex items-center space-x-2">
										<button
											onClick={handlePreviousPage}
											disabled={pagination.currentPage === 1 || activitiesLoading}
											className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:text-gray-500">
											<ChevronLeft className="h-4 w-4 mr-1" />
											Previous
										</button>
										<button
											onClick={handleNextPage}
											disabled={pagination.currentPage === pagination.totalPages || activitiesLoading}
											className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:text-gray-500">
											Next
											<ChevronRight className="h-4 w-4 ml-1" />
										</button>
									</div>
									<div className="text-sm text-gray-700">
										Page {pagination.currentPage} of {pagination.totalPages}
									</div>
								</div>
							)}
						</>
					)}
				</div>
			</div>

			{/* Sync Details Modal */}
			<SyncDetailsModal isOpen={showDetailsModal} onClose={handleCloseDetailsModal} activity={selectedActivity} />

			{/* Toast Notifications */}
			<ToastContainer toasts={toasts} onRemove={removeToast} />

			{/* Confirmation Dialog */}
			<ConfirmDialog
				isOpen={showConfirmDialog}
				title="Mark as Manually Triggered"
				message="Are you sure you want to mark this sync as manually triggered? This will change the status to indicate it needs to be handled manually from the source system."
				onConfirm={confirmMarkAsManuallyTriggered}
				onCancel={() => {
					setShowConfirmDialog(false);
					setPendingSyncLogId(null);
				}}
				confirmText="Mark as Manually Triggered"
				cancelText="Cancel"
			/>

			{/* Bulk Retry Confirmation Dialog */}
			<ConfirmDialog
				isOpen={showBulkConfirmDialog}
				title={
					bulkRetryMode === 'selected'
						? `Retry ${selectedSyncLogs.size} Selected Sync${selectedSyncLogs.size === 1 ? '' : 's'}`
						: bulkRetryMode === 'currentPage'
							? `Retry ${syncActivities.filter((a) => a.status === 'FAILED').length} Failed Syncs (Current Page)`
							: `Retry All ${totalFailedCount} Failed Syncs (All Pages)`
				}
				message={
					bulkRetryMode === 'selected'
						? `You are about to retry ${selectedSyncLogs.size} selected sync log${selectedSyncLogs.size === 1 ? '' : 's'}.\n\nThe syncs will be queued for automatic retry and processed in batches of 25.`
						: bulkRetryMode === 'currentPage'
							? `You are about to retry ${syncActivities.filter((a) => a.status === 'FAILED').length} failed sync${syncActivities.filter((a) => a.status === 'FAILED').length === 1 ? '' : 's'} from the current page.\n\nThe syncs will be queued for automatic retry and processed in batches of 25.`
							: totalFailedCount > syncActivities.filter((a) => a.status === 'FAILED').length
								? `You are about to retry ALL ${totalFailedCount} failed syncs across all pages${hasActiveFilters ? ' matching your filters' : ''}.\n\nThis will queue ${totalFailedCount} syncs for automatic retry. They will be processed in batches of 25. This may take some time to complete.`
								: `You are about to retry ${totalFailedCount} failed sync${totalFailedCount === 1 ? '' : 's'}.\n\nThe syncs will be queued for automatic retry and processed in batches of 25.`
				}
				onConfirm={confirmBulkRetry}
				onCancel={() => {
					setShowBulkConfirmDialog(false);
				}}
				confirmText={isBulkRetrying ? 'Processing...' : 'Yes, Retry Now'}
				cancelText="Cancel"
			/>
		</div>
	);
};
